import React, { useState, useEffect, useRef, useContext, ReactElement } from 'react';
import { CustomerApiProviderProps as Props, CustomerApi } from './constants';
import { CustomerApiRecord, LinkedId } from '../../api/customer/types';
import { CustomerClient } from '../../api/customer/CustomerClient';
import { useQuery } from 'react-query';

export const CustomerApiProvider = ({ linkedId: linkedIdProp, children }: Props): ReactElement<CustomerApi> => {
  const linkedId = useRef<LinkedId>(linkedIdProp);
  const [customer, setCustomer] = useState<CustomerApiRecord>();
  const [error, setError] = useState(false);
  const [getCustomerLoading, setGetCustomerLoading] = useState(true);

  const {
    data: customerData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['customerApiProvider', linkedId.current?.sourceSystemId],
    queryFn: ({ signal }) => CustomerClient.getCustomer(linkedId.current, signal),
    enabled: Boolean(linkedId.current?.sourceSystemId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (!linkedId.current?.sourceSystemId) {
      setGetCustomerLoading(false);
      return;
    }

    setGetCustomerLoading(isLoading);

    if (queryError || customerData?.error) {
      setError(true);
      setCustomer(undefined);
      return;
    }

    if (customerData) {
      setCustomer(customerData.data);
      setError(Boolean(customerData.error));
      if (customerData.error) {
        setCustomer(undefined);
      }
    }
  }, [customerData, isLoading]);

  return (
    <CustomerApi.Provider
      value={{
        customer: customer ?? {},
        setCustomer,
        loading: getCustomerLoading,
        error,
        linkedId: linkedId.current,
      }}
    >
      {children}
    </CustomerApi.Provider>
  );
};

export const useCustomerApi = (): CustomerApi => {
  const context = useContext(CustomerApi);
  if (!context?.customer) {
    throw new Error('useCustomerApi must be used within a CustomerApiProvider');
  }
  return context;
};
